import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { useStockTypes } from "@/contexts/StockTypesContext";
import { X, Plus, Save, Trash2, GripVertical } from "lucide-react";

const COLOR_OPTIONS = [
  "#228EBF",
  "#7A4DBA",
  "#1E8E8E",
  "#C2185B",
  "#B5651D",
  "#2C3E50",
  "#FFB300",
  "#001740",
  "#5FBE7E",
  "#9A6F00",
];

/**
 * Inline-editable list of departments. Manager-only.
 * Drag rows by the grip handle to reorder.
 */
const ManageDepartmentsDialog = ({ open, onClose }) => {
  const { reload } = useDepartments();
  const { types: stockTypes } = useStockTypes();
  const ALL_TYPES = stockTypes.map((t) => ({ val: t.name, label: t.label }));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLOR_OPTIONS[0]);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/departments");
      setRows(data || []);
    } catch (_) {
      toast.error("Could not load departments");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const create = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/departments", {
        name,
        color: newColor,
        sort: rows.length,
      });
      setNewName("");
      await load();
      await reload();
      toast.success(`Added ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.put(`/departments/${row.id}`, {
        name: row.name,
        color: row.color,
        allowed_types: row.allowed_types ?? null,
      });
      await load();
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.name}"?\nAny items or staff tagged with it will be set to Unassigned.`
      )
    )
      return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/departments/${row.id}`);
      await load();
      await reload();
      toast.success(
        `Removed "${row.name}" · detached ${data.detached_stock} items · ${data.detached_users} staff`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const updateRow = (id, patch) => {
    setRows((arr) => arr.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const onDragStart = (id) => () => setDragId(id);
  const onDragOver = (overId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    setRows((arr) => {
      const fromIdx = arr.findIndex((r) => r.id === dragId);
      const toIdx = arr.findIndex((r) => r.id === overId);
      if (fromIdx < 0 || toIdx < 0) return arr;
      const next = arr.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };
  const onDragEnd = async () => {
    if (!dragId) return;
    setDragId(null);
    try {
      await api.put("/departments/reorder", { ids: rows.map((r) => r.id) });
      await reload();
    } catch (_) {
      toast.error("Reorder failed — refresh and try again");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] overflow-y-auto"
      data-testid="manage-depts-overlay"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          className="nb-card p-5 w-full max-w-xl my-6"
          onClick={(e) => e.stopPropagation()}
          data-testid="manage-depts-dialog"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow">Organisation</div>
              <h3 className="font-display text-2xl mt-1">Departments</h3>
              <div className="label-eyebrow mt-1 text-[#607087]">
                Pick a colour, edit name, or delete — changes apply across the whole app.
              </div>
            </div>
            <button
              data-testid="manage-depts-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>

          {/* Existing departments */}
          <div className="flex flex-col gap-2 max-h-[55vh] overflow-y-auto scrollbar-clean pr-1 mb-4">
            {rows.length === 0 && (
              <div className="text-sm text-[#607087] italic py-4 text-center">
                No departments yet.
              </div>
            )}
            {rows.map((row) => {
              const allTypesAllowed = !Array.isArray(row.allowed_types) || row.allowed_types.length === 0;
              const toggleType = (t) => {
                const cur = Array.isArray(row.allowed_types) ? row.allowed_types.slice() : [];
                const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
                updateRow(row.id, { allowed_types: next });
              };
              const clearTypes = () => updateRow(row.id, { allowed_types: null });
              return (
                <div
                  key={row.id}
                  className={`nb-card-sm p-3 flex flex-col gap-2 transition-opacity ${
                    dragId === row.id ? "opacity-50" : ""
                  }`}
                  data-testid={`dept-row-${row.id}`}
                  draggable
                  onDragStart={onDragStart(row.id)}
                  onDragOver={onDragOver(row.id)}
                  onDragEnd={onDragEnd}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="cursor-grab active:cursor-grabbing text-[#607087] pl-0.5"
                      aria-label="drag handle"
                    >
                      <GripVertical size={14} />
                    </span>
                    <div className="flex items-center gap-1">
                      {COLOR_OPTIONS.map((c) => (
                        <button
                          key={c}
                          data-testid={`dept-color-${row.id}-${c}`}
                          onClick={() => updateRow(row.id, { color: c })}
                          className={`h-5 w-5 rounded-full transition-transform ${
                            row.color === c ? "scale-125 ring-2 ring-[#001740]/40" : ""
                          }`}
                          style={{ backgroundColor: c }}
                          type="button"
                          aria-label={`color ${c}`}
                        />
                      ))}
                    </div>
                    <input
                      data-testid={`dept-name-${row.id}`}
                      className="nb-input flex-1 h-9"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, { name: e.target.value })}
                    />
                    <button
                      data-testid={`dept-save-${row.id}`}
                      onClick={() => save(row)}
                      disabled={busy}
                      className="nb-btn nb-btn-primary p-2"
                      aria-label="save"
                    >
                      <Save size={14} strokeWidth={2.5} />
                    </button>
                    <button
                      data-testid={`dept-delete-${row.id}`}
                      onClick={() => remove(row)}
                      disabled={busy}
                      className="nb-btn nb-btn-danger p-2"
                      aria-label="delete"
                    >
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 pl-7">
                    <span className="label-eyebrow text-[#607087]">Allowed types:</span>
                    {ALL_TYPES.map((t) => {
                      const checked = allTypesAllowed || row.allowed_types?.includes(t.val);
                      return (
                        <button
                          key={t.val}
                          data-testid={`dept-type-${row.id}-${t.val}`}
                          type="button"
                          onClick={() => toggleType(t.val)}
                          className={`text-[11px] px-2 py-0.5 rounded-full font-display border transition-colors ${
                            checked
                              ? "bg-[#228EBF] text-white border-[#228EBF]"
                              : "bg-white text-[#607087] border-[#001740]/15"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                    {!allTypesAllowed && (
                      <button
                        type="button"
                        data-testid={`dept-types-reset-${row.id}`}
                        onClick={clearTypes}
                        className="text-[10px] underline text-[#607087] hover:text-[#001740] ml-1"
                      >
                        reset (allow all)
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add new */}
          <div className="nb-card-sm p-3" data-testid="dept-new-row">
            <div className="label-eyebrow mb-2">Add a department</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    data-testid={`dept-new-color-${c}`}
                    onClick={() => setNewColor(c)}
                    className={`h-5 w-5 rounded-full transition-transform ${
                      newColor === c ? "scale-125 ring-2 ring-[#001740]/40" : ""
                    }`}
                    style={{ backgroundColor: c }}
                    type="button"
                    aria-label={`color ${c}`}
                  />
                ))}
              </div>
              <input
                data-testid="dept-new-name"
                className="nb-input flex-1 h-10"
                placeholder="e.g. Scanners · IT · Spares"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !busy && create()}
              />
              <button
                data-testid="dept-new-add"
                onClick={create}
                disabled={busy || !newName.trim()}
                className="nb-btn nb-btn-primary py-2 px-3 flex items-center gap-1.5"
              >
                <Plus size={16} strokeWidth={2.5} /> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDepartmentsDialog;
